import { LoadingButton } from '@mui/lab';
import {
  Box,
  Container,
  Stack,
  Typography,
  FormLabel,
  TextField,
} from '@mui/material';
import _ from 'lodash';
import React, { ReactElement, useCallback, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';

import { resetPassword } from '@app/adapter/user-service';
import { HeadBlock } from '@app/components/Shared/HeadBlock';
import { getVerifyToken } from '@app/domain/network-actions';
import { useSetSnackbar } from '@app/hooks/useSetSnackbar';
import { ResetPasswordUpdateForm } from '@app/schemas/user';
import { UserPasswordUpdate } from '@app/types/user';

type ResetPasswordProps = {
  onSubmitCallBack: (newPassword: string) => Promise<void>;
  token: string;
};

function ResetPassword({ token, onSubmitCallBack }: ResetPasswordProps) {
  const [isLoading, setIsLoading] = useState(false);

  const {
    control,
    formState: { isValid },
    handleSubmit,
  } = useForm<UserPasswordUpdate>({
    defaultValues: ResetPasswordUpdateForm.defaultValues,
    mode: 'all',
    resolver: ResetPasswordUpdateForm.resolver,
  });

  const onSubmit = useCallback(
    async (data: UserPasswordUpdate) => {
      if (!data.newPasswords?.password) return;
      setIsLoading(true);
      await onSubmitCallBack(data.newPasswords.password);
      setIsLoading(false);
    },
    [onSubmitCallBack]
  );

  const formLabel = (label: string, require?: boolean): ReactElement => {
    return (
      <FormLabel>
        <Typography variant="body2">
          {label}
          {require && (
            <Typography component="span" color="error">
              *
            </Typography>
          )}
        </Typography>
      </FormLabel>
    );
  };

  return token ? (
    <>
      <HeadBlock />
      <Container>
        <Typography
          variant="h5"
          fontWeight={700}
          textAlign="center"
          sx={{ my: 4 }}
        >
          パスワード再設定
        </Typography>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={4}>
            <Typography>新しいパスワードを入力してください。</Typography>
            <Stack>
              {formLabel('新しいパスワード', true)}
              <Controller
                name="newPasswords.password"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    type="password"
                    error={!!error}
                    helperText={error?.message}
                    margin="dense"
                    placeholder="パスワードを入力"
                    fullWidth
                  />
                )}
              />
            </Stack>
            <Stack>
              {formLabel('新しいパスワードの確認', true)}
              <Controller
                name="newPasswords.confirmPassword"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    type="password"
                    error={!!error}
                    helperText={error?.message}
                    margin="dense"
                    placeholder="パスワードを入力"
                    fullWidth
                  />
                )}
              />
            </Stack>
            <Box>
              <LoadingButton
                type="submit"
                color="primary"
                variant="contained"
                fullWidth
                loading={isLoading}
                disabled={!isValid}
              >
                保存
              </LoadingButton>
            </Box>
          </Stack>
        </form>
      </Container>
    </>
  ) : null;
}

function WaitingReset() {
  return (
    <>
      <HeadBlock />
      <Container>
        <Stack spacing={4} sx={{ pt: 4 }}>
          <Typography
            fontWeight={700}
            variant="h4"
            textAlign="center"
            sx={{ px: 4 }}
          >
            パスワード再設定手続きのメールをお送りしました
          </Typography>
          <Typography>
            ご登録メールアドレス宛にパスワード再発行手続きのメールをお送りしました。
          </Typography>
          <Typography>
            メール本文に記述されているURLをクリックして頂き、パスワード再設定手続きを行ってください。
          </Typography>
        </Stack>
      </Container>
    </>
  );
}

export function WaitingForResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const setSnackbar = useSetSnackbar();
  const token = getVerifyToken();

  const onSubmitCallBack = useCallback(
    async (newPassword: string) => {
      const token = getVerifyToken();
      await resetPassword(newPassword, token)
        .then((data) => {
          if (data.status !== 204) {
            setSnackbar(true, 'パスワードの登録に失敗しました');
            return;
          }
          setSnackbar(true, 'パスワードを設定しました', 'success');
          navigate('/login');
        })
        .catch(() => {
          setSnackbar(true, 'パスワードの登録に失敗しました');
        });
    },
    [navigate, setSnackbar]
  );
  return (
    <>
      <HeadBlock />
      {location.state?.waiting && _.isEmpty(token) ? (
        <WaitingReset />
      ) : (
        <ResetPassword token={token} onSubmitCallBack={onSubmitCallBack} />
      )}
    </>
  );
}
